import { useEmailLogin, useUserFlow } from '@dispatch/web/auth';
import { DarkBox } from '@dispatch/web/ui';
import { Card, CardContent, CircularProgress, Alert } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import * as React from 'react';
import { useState } from 'react';
import { Location, useLocation } from 'react-router-dom';
import { string } from 'yup';

const loginSchema = string().email().nullable().required('Email is required');

interface LocationState {
  from: Location;
}

export default function LoginPage() {
  const { login } = useEmailLogin();
  const {
    determineFlow,
    isLoading: isFlowLoading,
    error: flowError,
  } = useUserFlow();
  const [email, setEmail] = useState('');
  const location = useLocation();
  const state = location?.state as LocationState;
  const [isValidEmail, setIsValidEmail] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateEmail = async () => {
    const isValid = await loginSchema.isValid(email);
    setIsValidEmail(isValid);
  };

  React.useEffect(() => {
    validateEmail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isValidEmail && !isSubmitting) {
      setIsSubmitting(true);
      try {
        // Determine the appropriate user flow based on email
        const authority = await determineFlow(email);
        // Proceed with login using the determined authority
        login(email, state?.from, authority);
      } catch (error) {
        // If flow determination fails, remain in error state
        // Do not proceed with login - stay on the page showing the error
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <DarkBox
      sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
      height="100vh"
    >
      <Container component="main" maxWidth="xs">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Card>
            <CardContent>
              <Typography component="h1" variant="h5">
                Sign in
              </Typography>
              {flowError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  Unable to process login request. Please try again later.
                  {process.env.NODE_ENV === 'development' && (
                    <Typography
                      variant="caption"
                      display="block"
                      sx={{ mt: 1 }}
                    >
                      {flowError}
                    </Typography>
                  )}
                </Alert>
              )}
              <Box
                component="form"
                onSubmit={handleSubmit}
                noValidate
                sx={{ mt: 1 }}
              >
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="email"
                  label="Email"
                  name="email"
                  autoComplete="email"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isSubmitting}
                />
                <Button
                  disabled={!isValidEmail || isSubmitting || isFlowLoading}
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="primary"
                  sx={{ mt: 3, mb: 1 }}
                >
                  {isSubmitting || isFlowLoading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    'NEXT'
                  )}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Container>
    </DarkBox>
  );
}
